import { Component } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Grid,
  Icon,
  Menu,
  Message,
  Modal,
  Popup,
  Tab,
} from 'semantic-ui-react';

import {
  getSubscriptions,
  getEmptyMessage,
  exportIndividualProject,
} from '../../store/actions';

import { CustomSegment, CustomTooltip, FeatureWrapper } from '../../components';
import { find, isEmpty, get, compact, findIndex } from '../../utils/lodash';
import { APP_NAME } from '../../utils/constant';
import { isFeature } from '../../utils/activity/userPermission';
import { Invoices, Subscription } from './forms';

import {
  OrganizationChecklistReport,
  OrganizationGLApproval,
  OrganizationGoals,
  OrganizationProfile,
} from '../organization/forms';

import './account.scss';

class ExportData extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modal: false,
    };
  }

  toggle() {
    this.setState((prevState) => ({
      modal: !prevState.modal,
    }));
  }

  async exportProjects() {
    await this.props.exportIndividualProject();
    const { individualProjectExport } = this.props;

    if (!isEmpty(individualProjectExport)) {
      window.open(individualProjectExport.download_link, '_blank');
    }
    this.toggle();
  }

  render() {
    const { t } = this.props;
    const { modal } = this.state;
    return (
      <Modal
        trigger={
          <Button
            icon="download"
            className="account-export"
            content={t('actions.export')}
            onClick={() => this.toggle()}
          />
        }
        size="tiny"
        open={modal}
        closeIcon={
          <Icon
            onClick={() => this.toggle()}
            name="close"
            className="closeicon"
          />
        }
      >
        <Modal.Header>{t('account.export_my_data')}</Modal.Header>
        <Modal.Content>
          <label className="billing-info">
            {t('account.all_your_activity_data_will_be_emailed')}
          </label>
          <div className="model-buttons">
            <Button
              className="billing-cancel"
              type="button"
              onClick={() => this.toggle()}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              loading={this.props.individualProjectExportLoading}
              disabled={this.props.individualProjectExportLoading}
              onClick={() => this.exportProjects()}
              content={t('actions.export')}
              className="billing"
            />
          </div>
        </Modal.Content>
      </Modal>
    );
  }
}

ExportData = withTranslation()(ExportData);

const Account = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [currentTab, setCurrentTab] = useState(0);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  const user = useSelector((state) => state.authentication.user);
  const emptyMessage = useSelector((state) => state.organization.emptyMessage);

  const goalEmpty = get(emptyMessage, 'goalEmpty', false);
  const helpTips = useSelector((state) => state.extra.activityHelpTips);

  const changeRoute = async (value) => {
    try {
      const tabs = generateTabs();
      const [tab, index] = await Promise.all([
        find(tabs, (o, i) => {
          return i === value || o.route === value;
        }),
        findIndex(tabs, (o, i) => {
          return i === value || o.route === value;
        }),
      ]);

      if (tab) {
        setCurrentTab(index);
        navigate(`/account/${tab.route}`);
      } else {
        setCurrentTab((state) => ({
          currentTab: state.prevState,
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    try {
      const { match } = props;
      if (match.params.type) {
        changeRoute(match.params.type);
      }
    } catch (error) {
      console.error(error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.title = `${t('common.account')} | ${APP_NAME}`;

    dispatch(getSubscriptions());
    dispatch(getEmptyMessage());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cardAlert = (visible) => {
    setVisible(visible);
  };

  const generateTabs = () => {
    const panes = compact([
      isFeature(user, 'account_profile')
        ? {
            menuItem: (
              <Menu.Item>
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  //TODO: change account help tip here
                  contentObject={{ data: helpTips, key: '194' }}
                  position="top left"
                  wide="very"
                >
                  <label>{t('common.profile')}</label>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'profile',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <CustomSegment
                  title={t('account.update_account_details')}
                  children={<OrganizationProfile account />}
                  className="update-organisation-details"
                />
              </Tab.Pane>
            ),
          }
        : null,
      isFeature(user, 'account_settings')
        ? {
            menuItem: (
              <Menu.Item>
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  //TODO: change account help tip here
                  contentObject={{ data: helpTips, key: '196' }}
                  position="top left"
                  wide="very"
                >
                  <label>{t('common.settings')}</label>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'settings',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <CustomSegment
                  title={t('account.account_settings')}
                  extraMargin
                  children={<OrganizationGLApproval account />}
                  className="update-organisation-details"
                />
                <FeatureWrapper
                  featureName="account_checklist"
                  children={
                    <div>
                      <CustomSegment
                        tooltip={{
                          user,
                          helpTips,
                          id: '172',
                        }}
                        extraMargin
                        title={t('common.checklist')}
                        children={<OrganizationChecklistReport account />}
                      />
                    </div>
                  }
                />
              </Tab.Pane>
            ),
          }
        : null,
      isFeature(user, 'account_billing')
        ? {
            menuItem: (
              <Menu.Item>
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  //TODO: change account help tip here
                  contentObject={{ data: helpTips, key: '205' }}
                  position="top left"
                  wide="very"
                >
                  <label>{t('common.billing')}</label>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'billing',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                {visible ? (
                  <Message
                    className="card-notification"
                    onDismiss={() => cardAlert(false)}
                    header={t('account.add_your_card_first')}
                    content={t(
                      'account.you_need_to_add_a_card_before_add_a_new_subscription_plan_click_the_add_card_button_to_start_your_subscription'
                    )}
                  />
                ) : null}
                <CustomSegment
                  title={t('account.subscription')}
                  children={
                    <Subscription cardAlert={(visible) => cardAlert(visible)} />
                  }
                  rightHeader={<ExportData {...props} />}
                  className="update-organisation-details"
                />
                <CustomSegment
                  extraMargin
                  title={t('account.invoices')}
                  children={
                    <Invoices cardAlert={(visible) => cardAlert(visible)} />
                  }
                />
              </Tab.Pane>
            ),
          }
        : null,
      isFeature(user, 'account_goals')
        ? {
            menuItem: (
              <Menu.Item>
                <div className="goal-tab-container">
                  {goalEmpty ? (
                    <Popup
                      position="bottom center"
                      content={<div>{t('common.goals_not_created')}</div>}
                      trigger={
                        <Icon
                          name="warning circle"
                          color="red"
                          size="large"
                        ></Icon>
                      }
                    />
                  ) : null}
                  <CustomTooltip
                    state={get(user, 'options.tips')}
                    //TODO: change account help tip here
                    contentObject={{
                      data: helpTips,
                      key: '206',
                    }}
                    position="top left"
                    wide="very"
                  >
                    <label>{t('common.goals')}</label>
                  </CustomTooltip>
                </div>
              </Menu.Item>
            ),
            route: 'goals',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                {/* <CustomSegment
                  title="SET ACCOUNT GOALS"
                  children={<OrganizationGoals account />}
                  className="ml-0 pl-0"
                  // className="update-organisation-details"
                /> */}
                <OrganizationGoals account />
              </Tab.Pane>
            ),
          }
        : null,
    ]);
    return panes;
  };

  const onTabClick = (data) => {
    changeRoute(data.activeIndex);
  };

  return (
    <Grid.Row>
      <Tab
        menu={{ borderless: true, attached: false, tabular: false }}
        panes={generateTabs()}
        className={isMobile ? 'custom-tabs-mobile' : 'custom-tabs'}
        activeIndex={currentTab}
        onTabChange={(e, data) => onTabClick(data)}
      />
    </Grid.Row>
  );
};

export default Account;
